import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

import { InputError } from "./MicroComponents";
import { CheckBox, InputWithLabel } from "../components/micro";
import FileUploadChoose from "./FileUploadChoose";
import {
  addDirectorShareholder,
  emailExist,
  existingUsers,
} from "../redux/companies/slice";
import { getCountryData } from "../redux/address/slice";
import { uploadFile } from "../redux/uploadFile/slice";
import { getOrganisationDetails } from "../redux/organisation/slice";

// Constants
const SHAREHOLDER_TYPE_OPTIONS = [
  { label: "Individual Shareholder", value: "individual" },
  { label: "Corporate Shareholder", value: "corporate" },
];

const CITIZENSHIP_OPTIONS = [
  { label: "Singaporean", value: "singaporean" },
  { label: "Permanent Resident", value: "permanent_resident" },
  { label: "Foreigners", value: "foreigners" },
];

// Helper component to check and auto-set registrable_controller
const RegistrableControllerChecker = ({
  showShareInfo,
  total_number_of_shares,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (!showShareInfo || !total_number_of_shares) return;

    const totalShares = Number(total_number_of_shares) || 0;
    if (totalShares <= 0) return;

    const numberShares = Number(values.number_of_shares) || 0;
    // const issuedCapital = Number(values.issued_share_capital) || 0;
    // const paidUpCapital = Number(values.paid_up_capital) || 0;

    // const sumOfShares = numberShares + issuedCapital + paidUpCapital;
    const twentyFivePercent = numberShares * 0.25;

    if (numberShares > twentyFivePercent && !values.registrable_controller) {
      setFieldValue("registrable_controller", true);
    }
  }, [
    values.number_of_shares,
    // values.issued_share_capital,
    // values.paid_up_capital,
    values.registrable_controller,
    total_number_of_shares,
    showShareInfo,
    setFieldValue,
  ]);

  return null;
};

const ShareholderForm = ({
  isEdit = {},
  onClose,
  onSuccess,
  company_id,
  shareholderArray = [],
  setShareholderArray,
  showShareInfo = true,
  showDocumentUpload = true,
  className = "",
  total_number_of_shares = "",
}) => {
  const dispatch = useDispatch();

  const [isLoad, setIsLoad] = useState(false);
  const [isFullNameDisabled, setIsFullNameDisabled] = useState(false);
  const [isCitizenshipDisabled, setIsCitizenshipDisabled] = useState(false);
  const [certificateDocuments, setCertificateDocuments] = useState([]);
  const [identityDocuments, setIdentityDocuments] = useState([]);

  // Selector
  const { existingUsersData, countryData, fileUploadLoading } = useSelector(
    (store) => ({
      existingUsersData: store?.companiesData?.existingUsersStatus?.data?.data,
      countryData: store?.addressData?.getCountryDataStatus?.data?.data,
      fileUploadLoading: store?.uploadFileData?.loading,
    })
  );

  useEffect(() => {
    dispatch(existingUsers());
    dispatch(getCountryData());
  }, [dispatch]);

  // Memoized existing users array - derived from existingUsersData
  const existingUsersArray = useMemo(
    () =>
      existingUsersData?.map((user) => ({
        ...user,
        label: user.full_name,
      })) || [],
    [existingUsersData]
  );

  // Country options for corporate shareholders
  const countryOptions = useMemo(
    () =>
      countryData?.map((country) => ({
        label: country.name,
        value: country.id,
      })) || [],
    [countryData]
  );

  // Initialize documents from isEdit
  useEffect(() => {
    if (isEdit?.corporate_documents?.length) {
      const documents = isEdit.corporate_documents.map((doc) => ({
        id: doc.company_document.document.id,
        name: doc.company_document.document.name,
      }));
      setCertificateDocuments(documents);
    }
    if (isEdit?.identity_documents?.length) {
      const documents = isEdit.identity_documents.map((doc) => ({
        id: doc.company_document.document.id,
        name: doc.company_document.document.name,
      }));
      setIdentityDocuments(documents);
    }
  }, [isEdit]);

  // Memoized debounced email check function
  const debouncedEmailCheck = useMemo(
    () =>
      debounce(async (email, setFieldValue) => {
        if (!email) return;

        try {
          const res = await dispatch(emailExist({ email }));
          const userData = res?.payload?.data;
          if (userData?.valid) {
            setIsFullNameDisabled(!!userData?.data?.full_name);
            setIsCitizenshipDisabled(!!userData?.data?.citizenship);
            setFieldValue("full_name", userData?.data?.full_name || "");
            setFieldValue("email", userData?.data?.email || "");
            setFieldValue("existing_user_id", userData?.data?.id || "");
            setFieldValue("citizenship", userData?.data?.citizenship || "");
          }
        } catch (err) {
          console.error("Email check failed", err);
        }
      }, 500),
    [dispatch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedEmailCheck.cancel();
    };
  }, [debouncedEmailCheck]);

  // Handle file upload for certificate documents
  const handleCertificateUpload = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      files.forEach((file) => {
        const formData = new FormData();
        formData.append("type", "shareholder-certificate");
        formData.append("file", file);
        formData.append(
          "document_type",
          file.type === "application/pdf" ? "doc" : "img"
        );
        dispatch(uploadFile(formData)).then((res) => {
          if (res.type === "uploadFile/fulfilled") {
            setCertificateDocuments((prev) => [...prev, res.payload.data]);
          }
        });
      });
    },
    [dispatch]
  );

  // Handle file upload for identity documents
  const handleIdentityUpload = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      files.forEach((file) => {
        const formData = new FormData();
        formData.append("type", "shareholder-identity");
        formData.append("file", file);
        formData.append(
          "document_type",
          file.type === "application/pdf" ? "doc" : "img"
        );
        dispatch(uploadFile(formData)).then((res) => {
          if (res.type === "uploadFile/fulfilled") {
            setIdentityDocuments((prev) => [...prev, res.payload.data]);
          }
        });
      });
    },
    [dispatch]
  );

  // Remove certificate document
  const removeCertificateDocument = useCallback((id) => {
    setCertificateDocuments((prev) => prev.filter((doc) => doc.id !== id));
  }, []);

  // Remove identity document
  const removeIdentityDocument = useCallback((id) => {
    setIdentityDocuments((prev) => prev.filter((doc) => doc.id !== id));
  }, []);

  // Form submission handler
  const handleFormSubmit = useCallback(
    (values) => {
      const payload = {
        ...(isEdit?.id && { id: isEdit.id }),
        existing_contact: values.existing_contact?.id,
        full_name: values.full_name,
        email: values.email,
        shareholder_type: values.shareholder_type,
        registrable_controller: values.registrable_controller ? 1 : 0,
        ...(values.citizenship && { citizenship: values.citizenship }),
        ...(values.country_id && { country_id: values.country_id }),
        ...(values.passport_fin_number && {
          passport_fin_number: values.passport_fin_number,
        }),
        ...(showShareInfo && {
          number_of_shares: values.number_of_shares,
          issued_share_capital: values.issued_share_capital,
          paid_up_capital: values.paid_up_capital,
        }),
        ...(certificateDocuments.length > 0 && {
          document_ids: certificateDocuments.map((doc) => doc.id),
        }),
        ...(identityDocuments.length > 0 && {
          identity_document_ids: identityDocuments.map((doc) => doc.id),
        }),
        company_id,
        ...(values.existing_user_id && {
          existing_user_id: values.existing_user_id,
        }),
        type: "shareholder",
      };

      setIsLoad(true);
      dispatch(addDirectorShareholder(payload)).then((res) => {
        setIsLoad(false);
        if (res.type === "addDirectorShareholder/fulfilled") {
          if (isEdit?.id) {
            const updatedArray = shareholderArray.map((shareholder) =>
              shareholder.id === isEdit.id ? res.payload.data : shareholder
            );
            setShareholderArray(updatedArray);
          } else {
            setShareholderArray([...shareholderArray, res.payload.data]);
          }
          toast.success(res.payload.message);
          onSuccess?.(res.payload.data);
          onClose?.();
          dispatch(getOrganisationDetails(company_id));
        }
      });
    },
    [
      isEdit,
      certificateDocuments,
      identityDocuments,
      company_id,
      shareholderArray,
      setShareholderArray,
      onSuccess,
      onClose,
      dispatch,
      showShareInfo,
    ]
  );

  // Email change handler
  const handleEmailChange = useCallback(
    (e, setFieldValue) => {
      const email = e.target.value;
      setFieldValue("email", email);
      debouncedEmailCheck(email, setFieldValue);
    },
    [debouncedEmailCheck]
  );

  // Field change handlers
  const handleShareholderTypeChange = useCallback((e, setFieldValue) => {
    setFieldValue("shareholder_type", e.value);
  }, []);

  const handleCitizenshipChange = useCallback((e, setFieldValue) => {
    setFieldValue("citizenship", e.value);
  }, []);

  const handleCountryChange = useCallback((e, setFieldValue) => {
    setFieldValue("country_id", e.value);
  }, []);

  const handleExistingContactChange = useCallback((e, setFieldValue) => {
    setFieldValue("existing_contact", e);
  }, []);

  // Cancel handler
  const handleCancel = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Initial form values
  const initialValues = useMemo(
    () => ({
      shareholder_type: isEdit?.shareholder_type ?? "",
      citizenship: isEdit?.citizenship ?? "",
      country_id: isEdit?.country_id ? Number(isEdit.country_id) : "",
      existing_contact: isEdit?.existing_contact ?? "",
      full_name: isEdit?.user?.full_name ?? "",
      email: isEdit?.user?.email ?? "",
      existing_user_id: isEdit?.existing_user_id ?? "",
      passport_fin_number: isEdit?.passport_fin_number ?? "",
      registrable_controller: isEdit?.registrable_controller === 1,
      number_of_shares: isEdit?.number_of_shares ?? "",
      issued_share_capital: isEdit?.issued_share_capital ?? "",
      paid_up_capital: isEdit?.paid_up_capital ?? "",
    }),
    [isEdit]
  );

  // Validation schema
  const validationSchema = useMemo(() => {
    const baseSchema = {
      shareholder_type: Yup.string().required(
        "Please Select Type of Shareholder"
      ),
      citizenship: Yup.string().when("shareholder_type", {
        is: "individual",
        then: Yup.string().required("Please Select Citizenship"),
        otherwise: Yup.string(),
      }),
      country_id: Yup.string().when("shareholder_type", {
        is: "corporate",
        then: Yup.string().required("Please Select Country of Incorporation"),
        otherwise: Yup.string(),
      }),
      existing_contact: Yup.object(),
      full_name: Yup.string().required("Please Enter Full Name"),
      email: Yup.string().required("Please Enter Email"),
      passport_fin_number: Yup.string().required(
        "Please Enter Passport/NRIC/FIN Number"
      ),
    };

    if (showShareInfo) {
      return Yup.object().shape({
        ...baseSchema,
        number_of_shares: Yup.number()
          .typeError("Number of Shares must be a valid number")
          .min(0, "Number of Shares must be 0 or greater")
          .required("Please Enter Number of Shares"),
        issued_share_capital: Yup.number()
          .typeError("Issued Share Capital must be a valid number")
          .min(0, "Issued Share Capital must be 0 or greater")
          .required("Please Enter Issued Share Capital"),
        paid_up_capital: Yup.number()
          .typeError("Paid-up Share Capital must be a valid number")
          .min(0, "Paid-up Share Capital must be 0 or greater")
          .required("Please Enter Paid-up Share Capital"),
      });
    }

    return Yup.object().shape(baseSchema);
  }, [showShareInfo]);

  // Memoized ReactSelect styles factory
  const getSelectStyles = useCallback(
    (hasError) => ({
      control: (base) => ({
        ...base,
        border: `1px solid ${hasError ? "red" : "#0000003B"}`,
        borderRadius: "0.25rem",
        padding: "6px 4px",
        boxShadow: "none",
        "&:hover": {
          border: "1px solid #0000003B",
        },
      }),
    }),
    []
  );

  return (
    <div
      className={`w-full space-y-3 max-h-[500px] overflow-y-auto ${className}`}
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              {/* Component to check and auto-set registrable_controller */}
              <RegistrableControllerChecker
                showShareInfo={showShareInfo}
                total_number_of_shares={total_number_of_shares}
              />
              <div className="space-y-4 py-3">
                <InputWithLabel
                  label="Email"
                  type="email"
                  className={
                    errors.email && touched.email ? "input-error" : "border"
                  }
                  idFromName="email"
                  onChange={(e) => handleEmailChange(e, setFieldValue)}
                  onBlur={handleBlur}
                  value={values.email}
                  errors={!!(errors.email && touched.email)}
                  errorsText={errors.email}
                />

                {/* Type of Shareholder */}
                <div>
                  <ReactSelect
                    className="text-black sm:text-base text-sm p-0"
                    id="shareholder_type"
                    placeholder="Type of Shareholder"
                    options={SHAREHOLDER_TYPE_OPTIONS}
                    getOptionValue={(option) => option.value}
                    value={SHAREHOLDER_TYPE_OPTIONS.find(
                      (op) => op.value === values.shareholder_type
                    )}
                    onBlur={handleBlur}
                    onChange={(e) =>
                      handleShareholderTypeChange(e, setFieldValue)
                    }
                    styles={getSelectStyles(
                      !!(errors.shareholder_type && touched.shareholder_type)
                    )}
                  />
                  {errors.shareholder_type && touched.shareholder_type && (
                    <InputError errorTitle={errors.shareholder_type} />
                  )}
                </div>

                {/* Citizenship for Individual Shareholders */}
                {values.shareholder_type === "individual" && (
                  <div>
                    <ReactSelect
                      className="text-black sm:text-base text-sm p-0"
                      id="citizenship"
                      placeholder="Citizenship"
                      options={CITIZENSHIP_OPTIONS}
                      isDisabled={isCitizenshipDisabled}
                      getOptionValue={(option) => option.value}
                      value={CITIZENSHIP_OPTIONS.find(
                        (op) => op.value === values.citizenship
                      )}
                      onBlur={handleBlur}
                      onChange={(e) =>
                        handleCitizenshipChange(e, setFieldValue)
                      }
                      styles={getSelectStyles(
                        !!(errors.citizenship && touched.citizenship)
                      )}
                    />
                    {errors.citizenship && touched.citizenship && (
                      <InputError errorTitle={errors.citizenship} />
                    )}
                  </div>
                )}

                {/* Country of Incorporation for Corporate Shareholders */}
                {values.shareholder_type === "corporate" && (
                  <div>
                    <ReactSelect
                      className="text-black sm:text-base text-sm p-0"
                      id="country_id"
                      placeholder="Country of Incorporation"
                      options={countryOptions}
                      getOptionValue={(option) => option.value}
                      value={countryOptions.find(
                        (op) => op.value === values.country_id
                      )}
                      onBlur={handleBlur}
                      onChange={(e) => handleCountryChange(e, setFieldValue)}
                      styles={getSelectStyles(
                        !!(errors.country_id && touched.country_id)
                      )}
                    />
                    {errors.country_id && touched.country_id && (
                      <InputError errorTitle={errors.country_id} />
                    )}
                  </div>
                )}

                {/* Existing Contact */}
                {existingUsersData?.length > 0 && (
                  <div>
                    <ReactSelect
                      className="text-black sm:text-base text-sm p-0"
                      id="existing_contact"
                      placeholder="Existing Contact"
                      options={existingUsersArray}
                      getOptionValue={(option) => option.value}
                      value={values.existing_contact}
                      onBlur={handleBlur}
                      onChange={(e) =>
                        handleExistingContactChange(e, setFieldValue)
                      }
                      styles={getSelectStyles(
                        !!(errors.existing_contact && touched.existing_contact)
                      )}
                    />
                    {errors.existing_contact && touched.existing_contact && (
                      <InputError errorTitle={errors.existing_contact} />
                    )}
                  </div>
                )}

                <InputWithLabel
                  label="Full Name"
                  type="text"
                  className={
                    errors.full_name && touched.full_name
                      ? "input-error"
                      : "border"
                  }
                  idFromName="full_name"
                  onChange={handleChange}
                  disabled={isFullNameDisabled}
                  onBlur={handleBlur}
                  value={values.full_name}
                  errors={!!(errors.full_name && touched.full_name)}
                  errorsText={errors.full_name}
                />

                {/* Passport/NRIC/FIN Number */}
                <InputWithLabel
                  label="Passport/NRIC/FIN Number"
                  type="text"
                  className={
                    errors.passport_fin_number && touched.passport_fin_number
                      ? "input-error"
                      : "border"
                  }
                  idFromName="passport_fin_number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.passport_fin_number}
                  errors={
                    !!(
                      errors.passport_fin_number && touched.passport_fin_number
                    )
                  }
                  errorsText={errors.passport_fin_number}
                />

                {/* Registrable Controller Checkbox */}
                <CheckBox
                  idFrom="registrable_controller"
                  label="Registrable Controller"
                  name="registrable_controller"
                  className="opacity-50 cursor-not-allowed pointer-events-none"
                  disabled={true}
                  onChange={(e) => {
                    setFieldValue("registrable_controller", e.target.checked);
                  }}
                  onBlur={handleBlur}
                  value={values.registrable_controller}
                  checked={values.registrable_controller ?? false}
                />

                {/* Individual Shareholder Share Information */}
                {showShareInfo && (
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Shareholder Share Information
                    </h3>

                    <InputWithLabel
                      label="Number of Shares"
                      type="number"
                      className={
                        errors.number_of_shares && touched.number_of_shares
                          ? "input-error"
                          : "border"
                      }
                      idFromName="number_of_shares"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.number_of_shares}
                      errors={
                        !!(errors.number_of_shares && touched.number_of_shares)
                      }
                      errorsText={errors.number_of_shares}
                    />

                    <InputWithLabel
                      label="Issued Share Capital"
                      type="number"
                      className={
                        errors.issued_share_capital &&
                        touched.issued_share_capital
                          ? "input-error"
                          : "border"
                      }
                      idFromName="issued_share_capital"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.issued_share_capital}
                      errors={
                        !!(
                          errors.issued_share_capital &&
                          touched.issued_share_capital
                        )
                      }
                      errorsText={errors.issued_share_capital}
                    />

                    <InputWithLabel
                      label="Paid-up Share Capital"
                      type="number"
                      className={
                        errors.paid_up_capital && touched.paid_up_capital
                          ? "input-error"
                          : "border"
                      }
                      idFromName="paid_up_capital"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.paid_up_capital}
                      errors={
                        !!(errors.paid_up_capital && touched.paid_up_capital)
                      }
                      errorsText={errors.paid_up_capital}
                    />
                  </div>
                )}

                {/* Certificate of Incorporation Documents for Corporate Shareholders */}
                {showDocumentUpload &&
                  values.shareholder_type === "corporate" && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Certificate of Incorporation Documents
                      </h3>
                      {certificateDocuments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {certificateDocuments.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between bg-gray-50 p-2 rounded"
                            >
                              <span className="text-sm text-gray-700">
                                {doc.name}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  removeCertificateDocument(doc.id)
                                }
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <FileUploadChoose
                        handleFileChange={handleCertificateUpload}
                        className="w-full"
                      />
                    </div>
                  )}

                {/* Identity Documents for All Shareholders */}
                {showDocumentUpload && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                      Identity Documents
                    </h3>
                    <p className="text-sm text-gray-600">
                      Upload scanned copies of NRIC/Passport/FIN documents
                    </p>
                    {identityDocuments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {identityDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded"
                          >
                            <span className="text-sm text-gray-700">
                              {doc.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeIdentityDocument(doc.id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <FileUploadChoose
                      handleFileChange={handleIdentityUpload}
                      className="w-full"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-4 w-full">
                  <button
                    type="button"
                    className="btn-outline uppercase"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoad || fileUploadLoading}
                    className={`${
                      isLoad || fileUploadLoading
                        ? "btn-outline-disabled"
                        : "btn-theme"
                    } uppercase`}
                  >
                    {isLoad || fileUploadLoading
                      ? "Loading"
                      : isEdit?.id
                      ? "Edit"
                      : "Add"}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ShareholderForm;
